import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

/**
 * SectionBreak Widget - Creates a page-wide section break with optional title
 * Forces widgets below it to align to the bottom of this section
 */
const SectionBreakWidget = ({ 
  widgetId, 
  config, 
  onRemove, 
  onConfigUpdate,
  isEditMode = false,
  onEditStart
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(config.title || '');
  const [isHovered, setIsHovered] = useState(false);

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const handleTitleSubmit = () => {
    setIsEditingTitle(false);
    // Save the title to the config
    if (onConfigUpdate) {
      onConfigUpdate(widgetId, { title: title });
    }
  };

  const handleTitleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    }
    if (e.key === 'Escape') {
      setIsEditingTitle(false);
      setTitle(config.title || '');
    }
  };

  const handleTitleClick = (e) => {
    // Ensure a single interaction immediately enters edit mode
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
      e.nativeEvent.stopImmediatePropagation();
    }
    if (isEditMode) {
      // Close the widget config sidebar if open
      if (onEditStart) {
        onEditStart();
      }
      setIsEditingTitle(true);
    }
  };

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    console.log('Remove button clicked for section break:', widgetId);
    
    // Remove directly without confirmation
    if (onRemove) {
      onRemove(widgetId);
    }
  };

  return (
    <div 
      className="w-full relative" 
      style={{ marginLeft: '-16px', marginRight: '-16px', width: 'calc(100% + 32px)' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-widget-loading="false"
    >
      {/* Section Break Line */}
      <div className="relative w-full">
        {/* Horizontal line */}
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-card-bd"></div>
        </div>
        
        {/* Title or edit button */}
        <div className="relative">
          {/* Drag Handle - appears on hover, positioned at top-left within boundaries */}
          {isEditMode && (
            <div 
              className={`section-break-drag-handle absolute left-8 top-4 w-5 h-5 flex items-center justify-center cursor-grab active:cursor-grabbing transition-opacity z-20 ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ 
                pointerEvents: isHovered ? 'auto' : 'none'
              }}
              onPointerDown={(e) => {
                // Prevent text selection when clicking drag handle
                e.stopPropagation();
              }}
              title="Drag to move section break"
            >
              <FontAwesomeIcon
                icon={icon({ name: 'grip-dots-vertical', family: 'sharp', style: 'light' })}
                className="text-text-3 hover:text-text-2 transition-colors text-lg"
              />
            </div>
          )}
          
          {isEditMode ? (
            <div 
              className={`bg-card flex justify-center items-center relative pr-10 rounded-card ${isEditingTitle ? 'border border-dashed border-border' : 'border border-card-bd'}`}
              style={{ marginLeft: '16px', marginRight: '16px', width: 'calc(100%-8px)' }}
              onPointerDownCapture={(e) => {
                // Stop propagation at capture phase to prevent parent handlers from interfering
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.closest?.('input') || e.target.closest?.('textarea')) {
                  e.stopPropagation();
                }
              }}
              onPointerMoveCapture={(e) => {
                // Stop propagation at capture phase to prevent parent handlers from interfering
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.closest?.('input') || e.target.closest?.('textarea')) {
                  e.stopPropagation();
                }
              }}
              onPointerUpCapture={(e) => {
                // Stop propagation at capture phase to prevent parent handlers from interfering
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.closest?.('input') || e.target.closest?.('textarea')) {
                  e.stopPropagation();
                }
              }}
            >
              {isEditingTitle ? (
                <input
                  type="text"
                  value={title}
                  onChange={handleTitleChange}
                  onBlur={handleTitleSubmit}
                  onKeyDown={handleTitleKeyPress}
                  onPointerDown={(e) => {
                    // Stop propagation to prevent parent handlers from interfering with text selection
                    e.stopPropagation();
                  }}
                  onPointerMove={(e) => {
                    // Stop propagation to prevent parent handlers from interfering with text selection
                    e.stopPropagation();
                  }}
                  onPointerUp={(e) => {
                    // Stop propagation to prevent parent handlers from interfering with text selection
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => {
                    // Stop propagation to prevent parent handlers from interfering with text selection
                    e.stopPropagation();
                  }}
                  onMouseMove={(e) => {
                    // Stop propagation to prevent parent handlers from interfering with text selection
                    e.stopPropagation();
                  }}
                  onMouseUp={(e) => {
                    // Stop propagation to prevent parent handlers from interfering with text selection
                    e.stopPropagation();
                  }}
                  className="cursor-text text-center text-xl font-semibold text-text bg-transparent focus:border-text focus:outline-none caret-text py-2 px-4"
                  style={{ width: 'calc(100% - 32px)' }}
                  placeholder="Section title..."
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  data-section-break-edit="true"
                  onMouseDown={handleTitleClick}
                  onTouchStart={handleTitleClick}
                  onClick={handleTitleClick}
                  className={`text-xl font-semibold ${title ? "text-text" : "text-text-3"} cursor-text hover:text-text-2 py-2 rounded-lg transition-colors px-4 text-center`}
                  style={{ width: 'calc(100% - 32px)' }}
                >
                  {title || 'Section break, click to add a title'}
                </button>
              )}
              {isEditMode && isHovered && onRemove && (
                <button
                  onClick={handleRemove}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  className="absolute top-0 right-2 text-text-2 hover:text-text transition-colors duration-200 rounded-full p-2 z-10 cursor-pointer"
                  title="Remove section break"
                  aria-label="Remove section break"
                  style={{ pointerEvents: 'auto' }}
                >
                  <FontAwesomeIcon
                    icon={icon({ name: 'xmark', family: 'sharp', style: 'light' })}
                    className="text-lg"
                  />
                </button>
              )}
            </div>
          ) : (
            title && (
              <div className="px-4" style={{ marginLeft: '16px', marginRight: '32px', width: 'calc(100%-8px)' }}>
                <h3 className="text-xl font-semibold text-text cursor-default text-center w-full">{title}</h3>
              </div>
            )
          )}
        </div>
      </div>

      {/* Spacer to create visual separation */}
      <div className="h-8"></div>
    </div>
  );
};

SectionBreakWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string
  }).isRequired,
  onRemove: PropTypes.func,
  onConfigUpdate: PropTypes.func,
  isEditMode: PropTypes.bool,
  onEditStart: PropTypes.func
};

export default SectionBreakWidget;
